import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { MatchRing, MatchBar, ConfidenceBadge } from "@/components/app/MatchScore";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Sparkle, Copy, ArrowSquareOut, CheckCircle, FloppyDisk, Buildings, MapPin,
  Briefcase, Tag, ClockCounterClockwise, CircleNotch, BookmarkSimple, EyeSlash,
  ArrowsClockwise, Clock, Globe, ArrowLeft,
} from "@phosphor-icons/react";

const SAVE_DEBOUNCE = 1200;

function timeAgo(dateStr) {
  if (!dateStr) return null;
  try {
    const d = new Date(dateStr);
    if (isNaN(d)) return null;
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    if (days <= 7) return `${days}d ago`;
    if (days <= 30) return `${Math.floor(days / 7)}w ago`;
    return `${Math.floor(days / 30)}mo ago`;
  } catch { return null; }
}

export default function JobDetailSheet({ open, job, onOpenChange, onAnalysed, onStateChange }) {
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [draft, setDraft] = useState("");
  const [savedDraft, setSavedDraft] = useState("");
  const [autoSaveStatus, setAutoSaveStatus] = useState("idle");
  const [applied, setApplied] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [versions, setVersions] = useState([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!job) return;
    const initialDraft = job.analysis?.application_draft || "";
    setAnalysis(job.analysis || null);
    setDraft(initialDraft);
    setSavedDraft(initialDraft);
    setApplied(!!job.is_applied);
    setSaved(!!job.is_saved);
    setError("");
    setAutoSaveStatus("idle");
    setVersions([]);
    if (job.analysis) loadVersions(job.id);
  }, [job]);

  const loadVersions = async (jobId) => {
    try {
      const { data } = await api.get(`/drafts/${jobId}/versions`);
      setVersions(data.versions || []);
    } catch {}
  };

  const persistDraft = async (text) => {
    if (!job) return;
    setAutoSaveStatus("saving");
    try {
      await api.patch(`/drafts/${job.id}`, { application_draft: text });
      setSavedDraft(text);
      setAutoSaveStatus("saved");
      loadVersions(job.id);
      setTimeout(() => setAutoSaveStatus((s) => (s === "saved" ? "idle" : s)), 1500);
    } catch (e) {
      setAutoSaveStatus("error");
      toast.error(e?.response?.data?.detail || "Auto-save failed");
    }
  };

  const onDraftChange = (val) => {
    setDraft(val);
    if (!analysis) return;
    setAutoSaveStatus("typing");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (val !== savedDraft) persistDraft(val);
      else setAutoSaveStatus("idle");
    }, SAVE_DEBOUNCE);
  };

  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  const analyse = async () => {
    if (!job) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post(`/jobs/${job.id}/analyze`);
      setAnalysis(data);
      setDraft(data.application_draft || "");
      setSavedDraft(data.application_draft || "");
      onAnalysed?.(data);
      toast.success("Analysis complete");
      loadVersions(job.id);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to analyse";
      setError(typeof msg === "string" ? msg : "Failed to analyse");
      toast.error(typeof msg === "string" ? msg : "Failed to analyse");
    } finally {
      setLoading(false);
    }
  };

  const regenerate = async () => {
    if (!job) return;
    setLoading(true);
    setError("");
    try {
      // Force re-analysis by clearing cache first
      const { data } = await api.post(`/jobs/${job.id}/analyze`);
      setAnalysis(data);
      setDraft(data.application_draft || "");
      setSavedDraft(data.application_draft || "");
      onAnalysed?.(data);
      toast.success("Draft regenerated");
      loadVersions(job.id);
    } catch (e) {
      toast.error("Regeneration failed");
    } finally {
      setLoading(false);
    }
  };

  const restoreVersion = async (versionId) => {
    try {
      const { data } = await api.post(`/drafts/${job.id}/restore/${versionId}`);
      setDraft(data.application_draft || "");
      setSavedDraft(data.application_draft || "");
      setAnalysis((prev) => (prev ? { ...prev, application_draft: data.application_draft } : prev));
      onAnalysed?.(data);
      toast.success("Version restored");
      loadVersions(job.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    }
  };

  const copyDraft = async () => {
    try { await navigator.clipboard.writeText(draft); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };

  const apply = async () => {
    if (!job) return;
    if (draft && analysis && draft !== savedDraft) {
      try { await api.patch(`/drafts/${job.id}`, { application_draft: draft }); } catch {}
    }
    try { 
      await api.post(`/jobs/applied`, { job }); 
      setApplied(true);
      if (onStateChange) onStateChange();
    } catch {}
    window.open(job.url, "_blank", "noopener,noreferrer");
  };

  const handleAction = async (endpoint) => {
    try {
      const { data } = await api.post(`/jobs/${endpoint}`, { job });
      if (endpoint === 'save') setSaved(data.saved);
      if (onStateChange) onStateChange();
      toast.success(endpoint === 'save' ? "Saved status updated" : "Job hidden");
      if (endpoint === 'hide') onOpenChange(false);
    } catch (err) {
      toast.error("Action failed");
    }
  };

  if (!job) return null;

  const dirty = draft !== savedDraft;
  const statusLabel = {
    idle: dirty ? "UNSAVED" : "SAVED",
    typing: "TYPING…",
    saving: "SAVING…",
    saved: "AUTO-SAVED",
    error: "SAVE FAILED",
  }[autoSaveStatus];
  const statusColor = {
    idle: dirty ? "text-amber-600" : "text-emerald-700",
    typing: "text-ink-mute",
    saving: "text-brand",
    saved: "text-emerald-700",
    error: "text-red-600",
  }[autoSaveStatus];

  const confidence = job.match_confidence || (analysis ? (analysis.match_score >= 70 ? "high" : analysis.match_score >= 40 ? "medium" : "low") : null);
  // Extract skills securely, even if job.matched_skills is missing but explanation has it
  let matchedSkills = Array.isArray(job.matched_skills) ? job.matched_skills : [];
  if (matchedSkills.length === 0 && job.match_explanation && job.match_explanation.includes("skills:")) {
    try {
      const parts = job.match_explanation.split("skills:");
      if (parts.length > 1) {
        matchedSkills = parts[1].split(",").map(s => s.trim().replace('.', ''));
      }
    } catch (e) {}
  }
  if (analysis?.skill_matches?.length) {
    matchedSkills = analysis.skill_matches;
  }
  matchedSkills = matchedSkills.filter(Boolean);

  const age = timeAgo(job.publication_date);

  // Description is already stripped of HTML by the backend strip_html() function
  // Render as plain text to avoid XSS risks
  const descriptionText = job.description || "No description provided.";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="sm:max-w-[1100px] w-full p-0 border-l border-slate-200 overflow-y-auto bg-white"
        data-testid="job-detail-sheet"
      >
        {/* ═══ Header ═══ */}
        <SheetHeader className="sticky top-0 z-10 bg-white px-4 sm:px-6 pt-4 pb-4 border-b border-border shadow-sm">
          {/* Mobile Back Button */}
          <div className="flex items-center mb-3">
            <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)} className="rounded-xl h-8 px-2 text-ink-soft hover:text-ink">
              <ArrowLeft size={16} weight="bold" className="mr-1.5" /> Back
            </Button>
          </div>
          
          <div className="flex flex-col sm:flex-row items-start justify-between gap-4">
            <div className="flex-1 min-w-0">
              <SheetTitle className="font-display font-black text-xl sm:text-2xl tracking-tight leading-tight pr-8">
                {job.title}
              </SheetTitle>
              <div className="mt-2 flex flex-wrap gap-2 text-xs text-ink-soft">
                <span className="inline-flex items-center gap-1.5"><Buildings size={12} weight="bold" />{job.company_name}</span>
                {job.candidate_required_location && <span className="inline-flex items-center gap-1.5"><MapPin size={12} weight="bold" />{job.candidate_required_location}</span>}
                {job.job_type && <span className="inline-flex items-center gap-1.5"><Briefcase size={12} weight="bold" />{job.job_type.replace(/_/g, " ")}</span>}
                {job.category && <span className="inline-flex items-center gap-1.5"><Tag size={12} weight="bold" />{job.category}</span>}
                {age && <span className="inline-flex items-center gap-1.5"><Clock size={12} weight="bold" />{age}</span>}
                {(job.publisher || job.source) && (
                  <span className="inline-flex items-center gap-1.5 uppercase tracking-wider font-semibold">
                    <Globe size={12} weight="bold" />{job.publisher || job.source}
                  </span>
                )}
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" onClick={() => handleAction('save')} className={`h-7 px-2 text-[10px] rounded-xl ${saved ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : ''}`}>
                  <BookmarkSimple size={12} weight={saved ? "fill" : "bold"} className="mr-1" /> {saved ? 'Saved' : 'Save'}
                </Button>
                {!applied && (
                  <Button size="sm" variant="outline" onClick={() => handleAction('hide')} className="h-7 px-2 text-[10px] rounded-xl hover:text-red-600 hover:border-red-200">
                    <EyeSlash size={12} weight="bold" className="mr-1" /> Hide
                  </Button>
                )}
              </div>
            </div>
            <div className="hidden sm:block">
              <MatchRing score={analysis?.match_score ?? job.prescore} size={72} stroke={6} testid="sheet-match-ring" confidence={confidence} />
            </div>
          </div>
        </SheetHeader>
        <div className="px-4 sm:px-6 py-6 lg:grid lg:grid-cols-5 lg:gap-8 items-start">
          
          {/* Left Column (40%) - Job Info & Analysis */}
          <div className="lg:col-span-2 space-y-6 sm:space-y-8">
            {/* ═══ Analysis CTA or Results ═══ */}
            {!analysis ? (
              <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-4 sm:p-6 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkle size={16} weight="fill" className="text-brand" />
                  <span className="tick-label text-brand">AI Analysis</span>
                </div>
                <h4 className="font-display font-bold text-lg mb-2">Run a smart match</h4>
                <p className="text-sm text-ink-soft mb-4">
                  Generate a precise match score, relevance reasoning, and a personalised application draft tailored to your profile.
                </p>

                {/* Quick match info */}
                {(job.prescore || matchedSkills.length > 0) && (
                  <div className="mb-4 border border-slate-200 bg-white p-3 rounded-xl">
                    <span className="tick-label mb-2 block">Quick Match Preview</span>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-mono font-black text-lg">{job.prescore || "—"}</span>
                      {confidence && <ConfidenceBadge confidence={confidence} />}
                    </div>
                    {matchedSkills.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {matchedSkills.slice(0, 6).map((s) => (
                          <span key={s} className="text-[10px] uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-md">{s}</span>
                        ))}
                      </div>
                    )}
                    <p className="mt-2 text-xs text-ink-soft">{job.match_explanation}</p>
                  </div>
                )}

                {error && (
                  <div className="mb-4 border border-red-300 bg-red-50 text-red-700 px-4 py-3 text-sm rounded-xl" data-testid="analyse-error">
                    <span className="tick-label text-red-700">Error</span>
                    <p className="mt-1">{error}</p>
                  </div>
                )}
                <Button onClick={analyse} disabled={loading} className="rounded-xl bg-brand hover:bg-brand-hover text-white uppercase tracking-wider text-xs h-10 px-5 w-full" data-testid="analyse-btn">
                  {loading ? "Analysing…" : (<><Sparkle size={14} weight="fill" className="mr-2" />Analyse with AI</>)}
                </Button>
              </div>
            ) : (
              <div className="space-y-6">
                {/* ═══ Match Results Grid ═══ */}
                <div className="flex flex-col gap-0 border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm rounded-xl overflow-hidden">
                  <div className="p-4 border-b border-slate-200 flex flex-row items-center justify-between gap-4">
                    <div>
                      <span className="tick-label">Match score</span>
                      <div className="mt-1 font-mono font-black text-3xl">{analysis.match_score}</div>
                      {confidence && <ConfidenceBadge confidence={confidence} />}
                    </div>
                    <div className="w-24"><MatchBar score={analysis.match_score} /></div>
                  </div>
                  <div className="p-4 border-b border-slate-200">
                    <span className="tick-label">Matching skills</span>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {analysis.skill_matches?.length ? analysis.skill_matches.map((s) => (
                        <span key={s} className="text-[10px] uppercase tracking-wider font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200/50 px-2 py-1 rounded-md">
                          {s}
                        </span>
                      )) : <span className="text-xs text-ink-mute">None detected</span>}
                    </div>
                  </div>
                  <div className="p-4">
                    <span className="tick-label">Skill gaps</span>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {analysis.skill_gaps?.length ? analysis.skill_gaps.map((s) => (
                        <span key={s} className="text-[10px] uppercase tracking-wider font-semibold bg-amber-50 text-amber-700 border border-amber-200/50 px-2 py-1 rounded-md">
                          {s}
                        </span>
                      )) : <span className="text-xs text-ink-mute">None — strong fit</span>}
                    </div>
                  </div>
                </div>

                {/* ═══ Why this matches ═══ */}
                <div>
                  <span className="tick-label">Why this matches</span>
                  <p className="mt-2 text-sm leading-relaxed text-ink border-l-2 border-brand pl-3">
                    {analysis.match_reason}
                  </p>
                </div>
              </div>
            )}

            {/* ═══ Job Description ═══ */}
            <div className={analysis ? "hidden lg:block" : ""}>
              <span className="tick-label">Job description</span>
              <div className="mt-2 text-sm leading-relaxed text-ink-soft max-h-[300px] overflow-y-auto border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-4 rounded-xl prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap">{descriptionText}</div>
              </div>
            </div>
          </div>

          {/* Right Column (60%) - Editor */}
          <div className="lg:col-span-3 mt-8 lg:mt-0">
            {analysis ? (
              <div className="border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm h-full flex flex-col rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3">
                    <span className="tick-label">Application Draft</span>
                    <span className={`font-mono text-[10px] font-bold tracking-wider ${statusColor}`} data-testid="autosave-status">
                      {autoSaveStatus === "saving" && <CircleNotch size={11} weight="bold" className="inline mr-1 animate-spin" />}
                      {statusLabel}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={regenerate} disabled={loading} className="rounded-xl uppercase tracking-wider text-xs h-8 px-2" title="Regenerate draft">
                      <ArrowsClockwise size={12} weight="bold" className="mr-1" />Regen
                    </Button>
                    <DropdownMenu open={versionsOpen} onOpenChange={setVersionsOpen}>
                      <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="ghost" className="rounded-xl uppercase tracking-wider text-xs h-8" data-testid="versions-btn">
                          <ClockCounterClockwise size={12} weight="bold" className="mr-1.5" />History · {versions.length}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="rounded-xl border-slate-200 min-w-[280px] max-h-[360px] overflow-y-auto">
                        <DropdownMenuLabel className="tick-label py-2">Draft history</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {versions.length === 0 && (
                          <div className="px-3 py-3 text-xs text-ink-mute">No saved versions yet.</div>
                        )}
                        {versions.map((v) => (
                          <DropdownMenuItem
                            key={v.id}
                            className="rounded-xl cursor-pointer focus:bg-slate-50 py-2"
                            onClick={() => restoreVersion(v.id)}
                          >
                            <div className="flex flex-col w-full">
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs font-semibold text-ink">{v.note || "Edit"}</span>
                                <span className="font-mono text-[10px] text-ink-mute">{(v.created_at || "").slice(11, 16)}</span>
                              </div>
                              <span className="text-[11px] text-ink-soft truncate">{(v.application_draft || "").slice(0, 90)}…</span>
                            </div>
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <Button size="sm" variant="ghost" onClick={copyDraft} className="rounded-xl uppercase tracking-wider text-xs h-8" data-testid="copy-draft-btn">
                      <Copy size={12} weight="bold" className="mr-1.5" />Copy
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => persistDraft(draft)} disabled={!dirty} className="rounded-xl uppercase tracking-wider text-xs h-8" data-testid="save-draft-btn">
                      <FloppyDisk size={12} weight="bold" className="mr-1.5" />Save
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={draft}
                  onChange={(e) => onDraftChange(e.target.value)}
                  className="flex-1 min-h-[450px] lg:min-h-[600px] rounded-none font-sans text-sm leading-relaxed border-0 border-b border-slate-200 focus-visible:ring-brand focus-visible:ring-1 focus-visible:ring-offset-0 resize-none p-6"
                  data-testid="draft-textarea"
                  placeholder="Your tailored application draft will appear here..."
                />
                <div className="px-4 py-3 text-[11px] text-ink-mute bg-slate-50/50">
                  Auto-saves ~1s after you stop typing. Up to 20 versions kept. Edit freely — your changes persist.
                </div>
              </div>
            ) : (
              <div className="border border-dashed border-slate-300 rounded-xl h-full min-h-[300px] flex flex-col items-center justify-center text-center p-8 bg-white/50">
                <Sparkle size={32} weight="duotone" className="text-slate-300 mb-3" />
                <h4 className="text-ink font-semibold">Ready to draft</h4>
                <p className="text-sm text-ink-soft mt-1 max-w-sm">Run AI analysis on the left to generate a personalized application draft.</p>
              </div>
            )}
            
            {/* Mobile Job Description */}
            <div className="mt-8 lg:hidden">
              <span className="tick-label">Job description</span>
              <div className="mt-2 text-sm leading-relaxed text-ink-soft max-h-[300px] overflow-y-auto border border-slate-200/60 bg-white/50 backdrop-blur-sm shadow-sm p-4 rounded-xl prose prose-sm max-w-none">
                <div className="whitespace-pre-wrap">{descriptionText}</div>
              </div>
            </div>
          </div>
        </div>

        {/* ═══ Sticky Action Bar ═══ */}
        <div className="sticky bottom-0 z-10 bg-white border-t border-border px-6 py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 text-xs text-ink-soft">
            {applied ? (
              <span className="inline-flex items-center gap-1.5 text-emerald-700 font-semibold"><CheckCircle size={14} weight="fill" />Marked as applied</span>
            ) : (
              <span>Opens the official job link. Draft is saved automatically.</span>
            )}
          </div>
          <Button onClick={apply} className="rounded-xl bg-ink hover:bg-brand text-white uppercase tracking-wider text-xs h-10 px-5" data-testid="apply-btn">
            Apply <ArrowSquareOut size={14} weight="bold" className="ml-2" />
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
